import { Button, Flex, Space } from 'antd'
import { EyeInvisibleOutlined, EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import React from 'react'
import { COLORS } from './colors.helper'
import Text from 'antd/es/typography/Text'
import { useThemeColors } from '../hooks/useThemeColors'
import PatternIcon from '../components/common/PatternIcon'

export const checkIfAnyModalIsOpen = () =>
    Boolean(
        document.querySelector('.ant-modal') &&
            !document.querySelector('.ant-modal').classList.contains('ant-modal-hidden'),
    )
export const checkIfAnyDropdownIsOpen = () => Boolean(document.querySelector('.ant-dropdown'))

export const passwordIconRender = visible =>
    visible ? (
        <Button type='text' style={{ padding: 0, height: 24 }} aria-label='hide password'>
            <EyeOutlined />
        </Button>
    ) : (
        <Button type='text' style={{ padding: 0, height: 24 }} aria-label='show password'>
            <EyeInvisibleOutlined />
        </Button>
    )

export const colorOptions = COLORS.map((color, index) => ({
    value: index,
    label: (
        <Space size={8}>
            <PatternIcon id={index} colorId={index} showId={false} />
            {color.label}
        </Space>
    ),
}))

export const renderOptionWithDescription = option => (
    <Flex vertical>
        <Text>{option.label}</Text>
        <Text type='secondary' style={{ wordWrap: 'break-word', whiteSpace: 'normal', fontWeight: 'normal' }}>
            {option.data.description}
        </Text>
    </Flex>
)

export const validateNoEmptySpaces = (_, value) => {
    if (value && value.trim()) return Promise.resolve()
    return Promise.reject(new Error('This field cannot contain only spaces or newlines'))
}

export const validateStrongPassword = (_, value) => {
    if (!value) return Promise.resolve();
    
    // Check minimum and maximum length
    if (value.length < 12) {
        return Promise.reject('Password must be at least 12 characters');
    }
    if (value.length > 64) {
        return Promise.reject('Password must be at most 64 characters');
    }
    
    // Check for uppercase letters
    if (!/[A-Z]/.test(value)) {
        return Promise.reject('Password must contain at least one uppercase letter');
    }
    
    // Check for lowercase letters
    if (!/[a-z]/.test(value)) {
        return Promise.reject('Password must contain at least one lowercase letter');
    }
    
    // Check for numbers
    if (!/[0-9]/.test(value)) {
        return Promise.reject('Password must contain at least one number');
    }
    
    // Check for special characters
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value)) {
        return Promise.reject('Password must contain at least one special character');
    }
    
    return Promise.resolve();
}

export const validatePasswordMatch = ({ getFieldValue }) => ({
    validator(_rule, value) {
        if (!value || getFieldValue('password') === value) {
            return Promise.resolve();
        }
        return Promise.reject('Passwords do not match');
    },
});

export const PasswordRequirements = ({ value = '' }) => {
    const colors = useThemeColors()
    const requirements = [
        {
            id: 'minLength',
            text: 'Minimum 12 characters',
            isMet: value.length >= 12
        },
        {
            id: 'lowercase',
            text: 'At least one lowercase letter',
            isMet: /[a-z]/.test(value)
        },
        {
            id: 'uppercase',
            text: 'At least one uppercase letter',
            isMet: /[A-Z]/.test(value)
        },
        {
            id: 'number',
            text: 'At least one number',
            isMet: /[0-9]/.test(value)
        },
        {
            id: 'specialChar',
            text: 'At least one special character',
            isMet: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value)
        }
    ];
    return (
        <Space direction='vertical' size={4}>
            {requirements.map(req => (
                <Space key={req.id}>
                    {req.isMet ? (
                        <CheckOutlined style={{ color: colors.success }} />
                    ) : (
                        <CloseOutlined />
                    )}
                    <Text type='secondary' style={{ color: req.isMet ? colors.success : undefined }}>
                        {req.text}
                    </Text>
                </Space>
            ))}
        </Space>
    );
};
